import { forwardRef } from 'react'
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch'

import styles from './ZoomableCanvas.module.scss'

/**
 * Zoomable + pannable canvas wrapper built on `react-zoom-pan-pinch`.
 *
 * Supports an optional `rotation` prop (degrees) to orient the content before
 * zoom is applied — intended for north-up floor plan display in TiersView and
 * future product plan viewers.  When `rotation` is 0 (default) no extra DOM
 * element is added.
 *
 * The forwarded ref exposes the full TransformWrapper API:
 *   { zoomIn, zoomOut, resetTransform, centerView, setTransform, ... }
 * — call `zoomRef.current?.zoomIn()` from the parent for +/- buttons.
 *
 * @param {object}   props
 * @param {React.ReactNode} props.children
 * @param {number}  [props.minScale=0.9]        - minimum zoom level
 * @param {number}  [props.maxScale=5]          - maximum zoom level
 * @param {boolean} [props.wheelDisabled=true]  - disable scroll-wheel zoom
 * @param {number}  [props.rotation=0]          - rotate inner content (degrees);
 *                                                0 = natural building orientation,
 *                                                compassAngle = north-up
 */
export const ZoomableCanvas = forwardRef(function ZoomableCanvas(
  { children, minScale = 0.9, maxScale = 5, wheelDisabled = true, rotation = 0 },
  ref,
) {
  return (
    <TransformWrapper
      ref={ref}
      minScale={minScale}
      maxScale={maxScale}
      wheel={{ disabled: wheelDisabled }}
      doubleClick={{ disabled: false }}
    >
      <TransformComponent
        wrapperStyle={{ width: '100%', height: '100%' }}
        contentStyle={{ width: '100%', height: '100%' }}
      >
        {rotation !== 0 ? (
          <div
            className={styles.rotated}
            style={{ transform: `rotate(${rotation}deg)` }}
          >
            {children}
          </div>
        ) : children}
      </TransformComponent>
    </TransformWrapper>
  )
})
